import Papa from 'papaparse';
import { supabase } from '../../../lib/supabase';
import { normaliseEposRow } from '../../../lib/csv/normaliseEposRow';

export function useCsvImport(orgId, userId) {
  const canonicalizeHeader = (hRaw) => {
    if (!hRaw) return '';
    let h = String(hRaw).trim();
    // If header contains metadata like "PLU,C,14" take the first token before comma
    const firstComma = h.indexOf(',');
    if (firstComma > 0) h = h.slice(0, firstComma);
    h = h.replace(/\s+/g, ' ').trim().toUpperCase();
    // Synonyms → canonical
    const map = new Map([
      ['SKU', 'PLU'],
      ['STOCKCODE', 'PLU'],
      ['PRODUCT CODE', 'PLU'],
      ['PRODUCTCODE', 'PLU'],
      ['ITEM CODE', 'PLU'],
      ['ITEMCODE', 'PLU'],
      ['DESCRIPTION', 'DESC'],
      ['PRODUCT', 'DESC'],
      ['NAME', 'DESC'],
      ['COST PRICE', 'COST'],
      ['UNIT COST', 'COST'],
      ['PRICE', 'COST'],
      ['UNIT PRICE', 'COST']
    ]);
    if (map.has(h)) return map.get(h);
    return h; // will be one of PLU/DESC/COST or other headers we ignore
  };
  const parsePreview = async (file, maxRows = 100) => new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      preview: maxRows,
      skipEmptyLines: 'greedy',
      worker: true,
      complete: (res) => {
        const origHeaders = res?.meta?.fields || [];
        const headers = origHeaders.map(canonicalizeHeader);
        const rows = (res?.data || []).map((r) => {
          const mapped = {};
          origHeaders.forEach((oh, i) => { mapped[headers[i]] = r[oh]; });
          return mapped;
        });
        resolve({ headers, rows });
      },
      error: (err) => reject(err)
    });
  });

  const importCsv = async (file, dryRun = true, onProgress) => new Promise((resolve, reject) => {
    const MAX_MB = 50 * 1024 * 1024;
    if (!file) return reject(new Error('No file selected'));
    if (file.size > MAX_MB) return reject(new Error('File exceeds 50 MB'));
    if (!orgId || !userId) return reject(new Error('Missing org or user context'));

    const required = ['PLU', 'DESC', 'COST'];
    let headersChecked = false;
    let total = 0, processed = 0, skipped = 0;
    const skippedDetails = [];
    const errors = [];
    const sampleSkus = new Set();
    const batch = [];
    const BATCH_SIZE = 2000;

    const flush = async () => {
      if (dryRun || batch.length === 0) return;
      const raw = batch.splice(0, batch.length);
      const deduped = new Map();
      for (const row of raw) deduped.set(row.sku, row);
      const payload = Array.from(deduped.values());
      // simple retry with exponential backoff
      let attempt = 0; const max = 3;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        try {
          const { error } = await supabase
            .from('epos_products')
            .upsert(payload, { onConflict: 'org_id,sku' });
          if (error) throw error;
          break;
        } catch (e) {
          console.error('[CSV upsert] attempt', attempt + 1, 'error:', e?.message || e, 'code:', e?.code, 'details:', e?.details, 'hint:', e?.hint);
          attempt++;
          if (attempt >= max) {
            errors.push({ reason: 'upsert-failed', message: e?.message || String(e), batch: payload.length });
            break;
          }
          await new Promise(r => setTimeout(r, 300 * (2 ** (attempt - 1))));
        }
      }
    };

    let headerMap = null; // origKey -> canonicalKey
    Papa.parse(file, {
      header: true,
      skipEmptyLines: 'greedy',
      step: async (results, parser) => {
        try {
          const rowRaw = results?.data || {};
          if (!headerMap) {
            headerMap = {};
            Object.keys(rowRaw).forEach((k) => { headerMap[k] = canonicalizeHeader(k); });
          }
          const row = {};
          Object.keys(rowRaw).forEach((k) => { row[headerMap[k]] = rowRaw[k]; });
          if (!headersChecked) {
            headersChecked = true;
            const cols = Object.keys(row);
            const missing = required.filter(c => !cols.includes(c));
            if (missing.length) {
              parser.abort();
              const err = new Error(`E001: Missing required columns: ${missing.join(', ')}`);
              err.code = 'E001';
              reject(err);
              return;
            }
          }
          total++;
          const n = normaliseEposRow(row, orgId, userId);
          if (!n) {
            skipped++;
            if (skippedDetails.length < 50) skippedDetails.push({ reason: 'invalid-required', row });
            return;
          }
          batch.push(n);
          processed++;
          if (sampleSkus.size < 10) sampleSkus.add(n.sku);
          if (onProgress) onProgress({ total, processed, skipped });
          if (!dryRun && batch.length >= BATCH_SIZE) {
            parser.pause();
            await flush();
            parser.resume();
          }
        } catch (e) {
          skipped++;
          const rawData = results?.data || {};
          const safeRow = headerMap ? Object.fromEntries(Object.keys(rawData).map(k => [headerMap[k] || k, rawData[k]])) : rawData;
          if (skippedDetails.length < 50) skippedDetails.push({ reason: e?.code || e?.message || 'parse-error', row: safeRow });
        }
      },
      complete: async () => {
        try {
          await flush();
          // record import history
          const summary = { total, processed, skipped, dryRun, filename: file.name, skippedDetails, errors };
          if (!dryRun) {
            await supabase.from('import_history').insert({
              org_id: orgId,
              filename: file.name,
              imported_by: userId,
              total_rows: total,
              skipped_rows: skipped,
              report: summary
            });
          }
          // best-effort verification: fetch a few SKUs
          if (!dryRun && sampleSkus.size) {
            const sample = Array.from(sampleSkus).slice(0, 5);
            const { data: verify, error: vErr } = await supabase
              .from('epos_products')
              .select('sku')
              .eq('org_id', orgId)
              .in('sku', sample);
            summary.verification = {
              sample,
              found: (verify || []).map(r => r.sku)
            };
          }
          onProgress?.({ total, processed, skipped, done: true });
          resolve(summary);
        } catch (e) {
          reject(e);
        }
      },
      error: (err) => reject(err)
    });
  });

  return { importCsv, parsePreview };
}
